/**
 * Subscription Tier System
 *
 * Defines the 3-tier subscription model (Standard, Premium, Elite) and
 * provides helpers for feature gating. Mirrors the permission system architecture.
 *
 * NULL tier defaults to "standard" — all existing agencies keep working unchanged.
 */

export type SubscriptionTier = 'standard' | 'premium' | 'elite';

const TIER_HIERARCHY: Record<SubscriptionTier, number> = {
  standard: 0,
  premium: 1,
  elite: 2,
};

export interface FeatureDefinition {
  minTier: SubscriptionTier;
  label: string;
  limits?: Partial<Record<SubscriptionTier, number>>;
}

/**
 * Feature-to-tier mapping. Single source of truth for all gated features.
 *
 * Feature keys are grouped by category but stored in a flat map.
 * `minTier` is the lowest tier that has access.
 * `limits` (optional) define numeric caps per tier.
 */
export const TIER_FEATURES: Record<string, FeatureDefinition> = {
  // --- Integrations ---
  tpa_carrier_payroll: { minTier: 'standard', label: 'TPA / Carrier / Payroll Integrations' },
  edi_834: { minTier: 'premium', label: '834 EDI Integration' },
  word_brown_api: { minTier: 'standard', label: 'Word & Brown API' },

  // --- Marketing ---
  marketing_basic: { minTier: 'standard', label: 'Basic Marketing Tools' },
  marketing_intermediate: { minTier: 'premium', label: 'Intermediate Marketing' },
  marketing_advanced: { minTier: 'premium', label: 'Advanced Marketing' },
  email_campaigns: {
    minTier: 'premium',
    label: 'Email Campaigns',
    limits: { premium: 150, elite: 250 },
  },
  sms_messaging: {
    minTier: 'premium',
    label: 'Text / SMS Messaging',
    limits: { premium: 200, elite: 300 },
  },

  // --- Compliance ---
  aca_basic: { minTier: 'standard', label: 'ACA Compliance (Basic View)' },
  compliance_checklist: { minTier: 'standard', label: 'Compliance Checklist' },
  benefits_compliance_wizard: { minTier: 'premium', label: 'Benefits Compliance Wizard' },
  benefits_compliance_report_card: { minTier: 'elite', label: 'Benefits Compliance Report Card' },
  hr_compliance_overview: { minTier: 'elite', label: 'HR Compliance Overview' },

  // --- Benefits & Enrollment ---
  new_hire_enrollment: { minTier: 'standard', label: 'New Hire & Open Enrollment' },
  open_enrollment: { minTier: 'standard', label: 'Open Enrollment' },
  life_events: { minTier: 'standard', label: 'Life Events & Beneficiary Tracking' },
  form_mapping: { minTier: 'standard', label: 'Form Mapping' },
  decision_support: { minTier: 'premium', label: 'Decision Support' },
  eoi_tracking: { minTier: 'premium', label: 'EOI Tracking' },
  eoi_integrated: { minTier: 'elite', label: 'EOI Integrated Management' },
  oe_one_click: { minTier: 'elite', label: 'One-Click Open Enrollment' },

  // --- Workflow ---
  email_sync: { minTier: 'premium', label: 'Integrated Email (Sync)' },
  calendaring: { minTier: 'premium', label: 'Calendaring' },
  calendly_feature: { minTier: 'elite', label: 'Calendly-like Feature' },
  task_management: { minTier: 'standard', label: 'Task Management' },
  email_ai: { minTier: 'elite', label: 'Email w/ AI Task Management' },

  // --- Management ---
  contact_directory: { minTier: 'standard', label: 'Contact Directory' },
  roles_management: { minTier: 'standard', label: 'Roles Management' },
  teams_management: { minTier: 'standard', label: 'Teams Management' },
  license_tracking: { minTier: 'premium', label: 'License Tracking' },
  customizable_branding: { minTier: 'premium', label: 'Customizable Branding' },
  service_history: { minTier: 'standard', label: 'Service History Tracking' },

  // --- Quoting ---
  instant_quote: { minTier: 'standard', label: 'Instant Quote' },
  advanced_quoting: { minTier: 'premium', label: 'Advanced Quoting' },
  rfp: { minTier: 'premium', label: 'RFP Build & Submit' },
  rfp_requests: { minTier: 'elite', label: 'RFP Requests' },
  sold_quote_conversion: { minTier: 'elite', label: 'Sold Quote Conversion' },
  sold_quote_automation: { minTier: 'premium', label: 'Sold Quote Enrollment Automation' },
  quote_tracking: { minTier: 'premium', label: 'Quote Tracking' },
  analytics_basic: { minTier: 'premium', label: 'Analytics' },
  analytics_coverage_gap: { minTier: 'elite', label: 'Analytics (Coverage Gap ID)' },
  oe_quoting: { minTier: 'premium', label: 'OE Quoting' },
  renewal_projections: { minTier: 'premium', label: 'Renewal Projections' },
};

/** All valid feature keys */
export type FeatureKey = keyof typeof TIER_FEATURES;

/**
 * Normalize a tier value. NULL / undefined / unknown values default to "standard".
 */
export function normalizeTier(tier: string | null | undefined): SubscriptionTier {
  if (tier === 'premium' || tier === 'elite' || tier === 'standard') {
    return tier;
  }
  return 'standard';
}

/**
 * Check if a tier has access to a feature (hierarchical).
 * A higher tier always includes all features of lower tiers.
 */
export function hasFeature(tier: string | null | undefined, feature: string): boolean {
  const def = TIER_FEATURES[feature];
  if (!def) return false;

  const normalizedTier = normalizeTier(tier);
  return TIER_HIERARCHY[normalizedTier] >= TIER_HIERARCHY[def.minTier];
}

/**
 * Get the numeric limit for a feature at a given tier, or null if unlimited / not applicable.
 */
export function getFeatureLimit(tier: string | null | undefined, feature: string): number | null {
  const def = TIER_FEATURES[feature];
  if (!def?.limits) return null;

  const normalizedTier = normalizeTier(tier);

  // If tier doesn't have access at all, return 0
  if (!hasFeature(normalizedTier, feature)) return 0;

  // Return the limit for the user's tier, or null if no limit defined for that tier (unlimited)
  return def.limits[normalizedTier] ?? null;
}

/**
 * Get the minimum tier required for a feature.
 */
export function getMinimumTierForFeature(feature: string): SubscriptionTier | null {
  const def = TIER_FEATURES[feature];
  return def?.minTier ?? null;
}

/**
 * Get display label for a tier.
 */
export function getTierLabel(tier: string | null | undefined): string {
  const normalized = normalizeTier(tier);
  const labels: Record<SubscriptionTier, string> = {
    standard: 'Standard',
    premium: 'Premium',
    elite: 'Elite',
  };
  return labels[normalized];
}

/** All valid tier values */
export const ALL_TIERS: SubscriptionTier[] = ['standard', 'premium', 'elite'];
